import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { HandCoins, Warning, X, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATEGORIES = [
  { v: "medical", label: "Medical", color: "#D95A41" },
  { v: "education", label: "Education", color: "#2C4A3B" },
  { v: "emergency", label: "Emergency", color: "#8A2E2E" },
  { v: "other", label: "Other", color: "#5C564D" },
];
const PRIORITIES = [
  { v: "low", label: "Low" }, { v: "medium", label: "Medium" },
  { v: "high", label: "High" }, { v: "urgent", label: "Urgent" },
];
const STATUS_COLOR = { open: "#D49A36", under_review: "#5C564D", approved: "#2C4A3B", fulfilled: "#2C4A3B", closed: "#8A2E2E" };

export default function Support() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!user || user === false) { setLoading(false); return; }
    setLoading(true);
    try { const { data } = await api.get("/support/requests"); setItems(data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [user]);

  if (!user || user === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <HandCoins size={40} className="mx-auto text-[#D95A41]" />
        <h1 className="font-heading text-4xl mt-4">Community support</h1>
        <p className="text-[#5C564D] mt-3">Please log in to raise or view a request.</p>
        <Link to="/login" className="samaaj-btn-primary mt-6 inline-block">Log in</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">Community support</div>
          <h1 className="font-heading text-5xl mt-2 tracking-tight">Ask for a <em className="text-[#D95A41] not-italic">helping hand</em>.</h1>
          <p className="text-[#5C564D] mt-2 max-w-2xl">Medical, education, or emergency — share your story and our admins will review and route funds to you.</p>
        </div>
        <button data-testid="btn-new-support" onClick={() => setShowForm(true)} className="samaaj-btn-primary">Raise a request</button>
      </div>

      <div className="mt-8 space-y-3" data-testid="support-list">
        {loading ? <div className="text-[#5C564D]">Loading…</div> :
          items.length === 0 ? <div className="samaaj-card p-10 text-center text-[#5C564D]">No requests yet.</div> :
          items.map((r) => (
            <div key={r.id} className="samaaj-card p-5" data-testid={`req-${r.id}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-heading text-xl">{r.title}</div>
                  <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-2 flex-wrap">
                    <span className="samaaj-chip capitalize">{r.category}</span>
                    <span className="samaaj-chip" style={{ background: r.priority === "urgent" ? "#F5C7C0" : "#EFE4D3", color: r.priority === "urgent" ? "#8A2E2E" : "#5C564D" }}>{r.priority}</span>
                    <span className="font-bold uppercase tracking-wider" style={{ color: STATUS_COLOR[r.status] || "#5C564D" }}>{r.status.replace("_", " ")}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-[#5C564D]">Needed</div>
                  <div className="font-heading text-2xl text-[#D95A41]">${(+r.amount_needed).toFixed(2)}</div>
                  {r.raised > 0 && <div className="text-xs text-[#2C4A3B] font-semibold">Raised ${(+r.raised).toFixed(2)}</div>}
                </div>
              </div>
              <p className="text-sm text-[#5C564D] mt-3 leading-relaxed">{r.story}</p>
              {r.admin_note && <div className="text-xs mt-2 p-2 rounded bg-[#F2EBE5] text-[#5C564D]"><strong>Admin note:</strong> {r.admin_note}</div>}
            </div>
          ))
        }
      </div>

      {showForm && <NewRequest onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function NewRequest({ onClose, onDone }) {
  const [f, setF] = useState({ category: "medical", priority: "medium", title: "", story: "", amount_needed: 500, contact_phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      await api.post("/support/requests", { ...f, amount_needed: Number(f.amount_needed) });
      toast.success("Request submitted — admins have been notified");
      onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#1C1B1A]/60 flex items-center justify-center p-4 overflow-y-auto">
      <div className="samaaj-card max-w-lg w-full p-6 bg-[#F9F6F0] my-8">
        <div className="flex items-start justify-between">
          <div className="font-heading text-3xl">Request support</div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#F2EBE5]"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="mt-4 grid grid-cols-2 gap-3">
          <select data-testid="sup-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]">
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
          </select>
          <select data-testid="sup-priority" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]">
            {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
          </select>
          <input data-testid="sup-title" required placeholder="Short title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="sup-amount" required type="number" step="0.01" placeholder="Amount needed (USD)" value={f.amount_needed} onChange={(e) => setF({ ...f, amount_needed: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <input data-testid="sup-phone" placeholder="Phone (optional)" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} className="px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <textarea data-testid="sup-story" required rows={5} placeholder="Share your story — what has happened and how the community can help" value={f.story} onChange={(e) => setF({ ...f, story: e.target.value })} className="col-span-2 px-3 py-2 rounded-xl border border-[#E5D9CC]" />
          <div className="col-span-2 flex items-start gap-2 p-3 rounded-xl bg-[#FCF3E1] border border-[#E9D9AF] text-xs text-[#5C564D]">
            <Warning size={14} weight="fill" className="text-[#D49A36] mt-0.5 shrink-0" />
            <span>All requests are reviewed by community admins before funds are moved. Please share truthful details — false claims will be closed.</span>
          </div>
          <button data-testid="sup-submit" disabled={busy} className="samaaj-btn-primary col-span-2">
            {busy ? <><CircleNotch className="inline animate-spin" size={14} /> Sending…</> : "Send request"}
          </button>
        </form>
      </div>
    </div>
  );
}
